// src/pages/home/Home.tsx
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import HeroSlider from '../../components/sections/HeroSlider';
import CategoriesSection from '../../components/sections/CategoriesSection';
import FeaturedProductsSection from '../../components/sections/FeaturedProductsSection';
import { useShopContext } from '../../context/ShopContext';
import { useProductContext } from '../../context/ProductContext';
import { ogDefault } from '../../images';
import StoreJsonLd from '../../components/seo/StoreJsonLd';
import BreadcrumbJsonLd from '../../components/seo/BreadcrumbJsonLd';
import EnhancedProductsJsonLd from '../../components/seo/EnhancedProductsJsonLd';

const HomePage: React.FC = () => {
  const { error: shopError } = useShopContext();
  const { error: productError, fetchProducts, products } = useProductContext();
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
    fetchProducts();
  }, []);

  const getOrigin = () => {
    if (typeof window !== 'undefined') {
      return window.location.origin;
    }
    return 'https://bazaar-marketplace.com';
  };

  const origin = getOrigin();

  if (shopError || productError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Helmet>
          <title>خطأ في تحميل المتجر | Bazaar Marketplace - منصة المتاجر المتعددة</title>
          <meta name="robots" content="noindex, follow" />
          <link rel="icon" href="/favicon.ico" />
        </Helmet>
        <p className="text-red-500 text-xl">{shopError || productError}</p>
      </div>
    );
  }

  const limitedProducts = products.slice(0, 8);

  return (
    <div className="bg-gray-50">
      <StoreJsonLd />
      
      {products.length > 0 && isClient && (
        <EnhancedProductsJsonLd products={limitedProducts} />
      )}

      <BreadcrumbJsonLd
        items={[
          { name: "الرئيسية", url: origin },
          { name: "الشروط والأحكام", url: `${origin}/terms-conditions` },
          { name: "السياسات", url: `${origin}/polices` },
          { name: "المنتجات", url: `${origin}/products` },
          { name: "متجر 3m", url: `${origin}/store/3m` }
        ]}
      />

      <Helmet>
        <html lang="ar" dir="rtl" />
        
        <title>Bazaar Marketplace - منصة متاجر متعددة: تسوق من 3m وغيرها من المتاجر في مصر</title>
        
        <meta
          name="description"
          content="منصة Bazaar Marketplace: تسوق آمن وسريع من آلاف المتاجر مثل 3m ومتاجر أخرى. نوصل لجميع محافظات مصر بأقل الأسعار. منصة موثوقة للتسوق والبيع الإلكتروني مع دعم فني متواصل."
        />
        
        <meta name="keywords" content="Bazaar Marketplace, متاجر, تسوق أونلاين, شراء عبر الإنترنت, 3m, سوق إلكتروني, منصة بيع, متاجر متعددة, مصر, توصيل سريع, أسعار منافسة, دعم فني, سياسات شفافة" />
        
        <meta name="geo.region" content="EG" />
        <meta name="geo.placename" content="مصر" />
        <meta name="geo.position" content="30.0444;31.2357" />
        <meta name="ICBM" content="30.0444, 31.2357" />
        
        <meta name="contact" content="support@bazaar-marketplace.com" />
        <meta name="telephone" content="+201503323006" />
        
        <link rel="privacy-policy" href={`${origin}/polices`} />
        <link rel="terms-of-service" href={`${origin}/terms-conditions`} />
        
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="author" content="Bazaar Marketplace Team" />
        <meta name="copyright" content="Bazaar Marketplace" />
        
        <link rel="canonical" href={origin + '/'} />
        <link rel="alternate" href={origin} hrefLang="ar" />
        <link rel="alternate" href={`${origin}/en`} hrefLang="en" />
        <link rel="alternate" href={origin} hrefLang="x-default" />

        <meta property="og:title" content="Bazaar Marketplace - منصة المتاجر المتعددة في مصر" />
        <meta
          property="og:description"
          content="تسوق من متاجر مثل 3m وغيرها على Bazaar Marketplace. نوصل لجميع أنحاء مصر بضمان المنتجات وأسعار تنافسية. دعم فني: 01503323006"
        />
        <meta property="og:url" content={origin + '/'} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={`${origin}/images/og-homepage.jpg`} />
        <meta property="og:image:alt" content="منصة Bazaar Marketplace للمتاجر المتعددة" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="Bazaar Marketplace" />
        <meta property="og:locale" content="ar_EG" />
        <meta property="og:email" content="support@bazaar-marketplace.com" />
        <meta property="og:phone_number" content="+201503323006" />
        <meta property="og:see_also" content="https://www.facebook.com/profile.php?id=61580674130494" />
        <meta property="og:see_also" content="https://www.instagram.com/bazaar_marketplace/" />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Bazaar Marketplace - تسوق من 3m ومتاجر أخرى" />
        <meta
          name="twitter:description"
          content="منصة موثوقة للتسوق من متاجر متعددة في مصر. نوصل لباب بيتك بأفضل الأسعار. للدعم: support@bazaar-marketplace.com"
        />
        <meta name="twitter:image" content={`${origin}/images/twitter-card.jpg`} />
        <meta name="twitter:label1" content="الدعم الفني" />
        <meta name="twitter:data1" content="01503323006" />
        <meta name="twitter:label2" content="البريد الإلكتروني" />
        <meta name="twitter:data2" content="support@bazaar-marketplace.com" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            "@id": `${origin}/#website`,
            "url": origin,
            "name": "Bazaar Marketplace",
            "description": "منصة متاجر متعددة للتسوق والبيع في مصر",
            "inLanguage": "ar",
            "publisher": {
              "@type": "Organization",
              "@id": `${origin}/#organization`,
              "name": "Bazaar Marketplace",
              "logo": `${origin}/logo.png`,
              "contactPoint": {
                "@type": "ContactPoint",
                "telephone": "+201503323006",
                "contactType": "customer service",
                "email": "support@bazaar-marketplace.com",
                "availableLanguage": ["Arabic", "English"]
              }
            }
          })}
        </script>
        
        {isClient && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Organization",
              "@id": `${origin}/#organization`,
              "name": "Bazaar Marketplace",
              "alternateName": "بازار ماركت بليس",
              "url": origin,
              "logo": `${origin}/logo.png`,
              "foundingDate": "2023",
              "founders": [
                {
                  "@type": "Person",
                  "name": "فريق Bazaar Marketplace"
                }
              ],
              "address": {
                "@type": "PostalAddress",
                "addressCountry": "EG",
                "addressRegion": "القاهرة",
                "addressLocality": "القاهرة الجديدة",
                "postalCode": "11511",
                "streetAddress": "شارع التسعين، القاهرة الجديدة"
              },
              "contactPoint": [
                {
                  "@type": "ContactPoint",
                  "telephone": "+201503323006",
                  "contactType": "customer service",
                  "email": "support@bazaar-marketplace.com",
                  "availableLanguage": ["Arabic", "English"],
                  "hoursAvailable": {
                    "@type": "OpeningHoursSpecification",
                    "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
                    "opens": "09:00",
                    "closes": "23:00"
                  }
                }
              ],
              "sameAs": [
                "https://www.facebook.com/profile.php?id=61580674130494",
                "https://www.instagram.com/bazaar_marketplace/"
              ],
              "knowsAbout": [
                "التجارة الإلكترونية",
                "منصات التسوق",
                "متاجر متعددة",
                "التوصيل في مصر",
                "3m products",
                "Online marketplace"
              ],
              "areaServed": {
                "@type": "Country",
                "name": "Egypt"
              },
              "legalName": "Bazaar Marketplace Egypt",
              "taxID": "752-382-462",
              "vatID": "EG-752-382-462"
            })}
          </script>
        )}

        {isClient && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": [
                {
                  "@type": "Question",
                  "name": "ما هي منصة Bazaar Marketplace؟",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Bazaar Marketplace هي منصة متاجر متعددة تتيح للبائعين إنشاء متاجرهم الإلكترونية وللمتسوقين شراء منتجات متنوعة من مختلف المتاجر مثل 3m وآلاف المتاجر الأخرى في مكان واحد."
                  }
                },
                {
                  "@type": "Question",
                  "name": "كيف يمكنني التواصل مع الدعم الفني؟",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "يمكنك التواصل مع فريق الدعم عبر البريد الإلكتروني: support@bazaar-marketplace.com أو الاتصال على الرقم: 01503323006. نحن متاحون يومياً من 9 صباحاً حتى 11 مساءً."
                  }
                },
                {
                  "@type": "Question",
                  "name": "هل يمكنني التسوق من متجر 3m عبر المنصة؟",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "نعم، يمكنك تصفح وشراء جميع منتجات متجر 3m بالإضافة إلى آلاف المتاجر الأخرى على منصة Bazaar Marketplace. كل منتج يتم توصيله بضمان الجودة."
                  }
                },
                {
                  "@type": "Question",
                  "name": "ما هي سياسات الاسترجاع والاستبدال؟",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "يمكنك الاطلاع على جميع سياسات الاسترجاع والاستبدال والخصوصية من خلال زيارة: https://bazaar-marketplace.com/polices"
                  }
                },
                {
                  "@type": "Question",
                  "name": "ما هي مناطق التوصيل المتاحة؟",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "نوصل لجميع محافظات مصر. يمكنك اختيار مدينة التوصيل عند الشراء من أي متجر على المنصة. معظم المناطق توصيل خلال 2-5 أيام عمل."
                  }
                },
                {
                  "@type": "Question",
                  "name": "كيف يمكنني قراءة الشروط والأحكام؟",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "يمكنك الاطلاع على الشروط والأحكام الكاملة للمنصة من خلال: https://bazaar-marketplace.com/terms-conditions"
                  }
                },
                {
                  "@type": "Question",
                  "name": "أين يمكنني متابعة أخبار المنصة؟",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "يمكنك متابعتنا على فيسبوك: https://www.facebook.com/profile.php?id=61580674130494 وعلى إنستجرام: https://www.instagram.com/bazaar_marketplace/"
                  }
                }
              ]
            })}
          </script>
        )}

        {isClient && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "HowTo",
              "name": "كيفية التسوق على Bazaar Marketplace",
              "description": "خطوات سهلة للتسوق من متاجر مثل 3m على المنصة",
              "estimatedCost": {
                "@type": "MonetaryAmount",
                "currency": "EGP",
                "value": "0"
              },
              "supply": [
                {
                  "@type": "HowToSupply",
                  "name": "اتصال بالإنترنت"
                },
                {
                  "@type": "HowToSupply",
                  "name": "جهاز متصل بالإنترنت"
                }
              ],
              "tool": [
                {
                  "@type": "HowToTool",
                  "name": "متصفح ويب أو تطبيق جوال"
                }
              ],
              "step": [
                {
                  "@type": "HowToStep",
                  "name": "زيارة الموقع",
                  "text": "اذهب إلى https://bazaar-marketplace.com",
                  "url": origin,
                  "image": `${origin}/images/step1.jpg`
                },
                {
                  "@type": "HowToStep",
                  "name": "تصفح المتاجر",
                  "text": "استعرض المتاجر المتاحة مثل 3m وغيرها",
                  "url": `${origin}/stores`,
                  "image": `${origin}/images/step2.jpg`
                },
                {
                  "@type": "HowToStep",
                  "name": "اختر المنتجات",
                  "text": "أضف المنتجات التي تريدها إلى عربة التسوق",
                  "image": `${origin}/images/step3.jpg`
                },
                {
                  "@type": "HowToStep",
                  "name": "اتمام الشراء",
                  "text": "اتبع خطوات الدفع واستلم منتجاتك",
                  "image": `${origin}/images/step4.jpg`
                },
                {
                  "@type": "HowToStep",
                  "name": "المتابعة والدعم",
                  "text": "للأسئلة أو الدعم: support@bazaar-marketplace.com أو 01503323006",
                  "url": `${origin}/contact`,
                  "image": `${origin}/images/step5.jpg`
                }
              ],
              "totalTime": "PT10M"
            })}
          </script>
        )}

        {isClient && products.length > 0 && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "ItemList",
              "itemListElement": limitedProducts.map((product, index) => ({
                "@type": "ListItem",
                "position": index + 1,
                "url": `${origin}/product/${product.product.id}`
              }))
            })}
          </script>
        )}
      </Helmet>

      <h1 className="sr-only">
        Bazaar Marketplace - منصة المتاجر المتعددة الأولى في مصر: تسوق من 3m ومتاجر أخرى بأفضل الأسعار
      </h1>
      <p className="sr-only">
        اكتشف آلاف المنتجات من متاجر مثل 3m ومتاجر أخرى على منصة Bazaar Marketplace. 
        تسوق آمن مع توصيل لجميع محافظات مصر وضمان على المنتجات. 
        أسعار تنافسية وعروض حصرية يومية. 
        للدعم الفني: 01503323006 أو support@bazaar-marketplace.com
      </p>
      <p className="sr-only">
        سياسات شفافة: اقرأ الشروط والأحكام من https://bazaar-marketplace.com/terms-conditions
        و السياسات من https://bazaar-marketplace.com/polices
      </p>
      <p className="sr-only">
        تابعنا على فيسبوك: https://www.facebook.com/profile.php?id=61580674130494
        وإنستجرام: https://www.instagram.com/bazaar_marketplace/
      </p>

      <HeroSlider />
      <CategoriesSection />
      <FeaturedProductsSection />
    </div>
  );
};

export default HomePage;